import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Grid2,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { Fragment, memo, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowBackIos,
  ArrowBackIosNew,
  ArrowForwardIos,
  DriveFileRenameOutlineOutlined,
  Info,
  Refresh,
} from "@mui/icons-material";
import UsageChart from "../../components/layouts/UsageChart";
import { callApiAction } from "../../store/actions/commonAction";
import CustomBarChart from "../../components/layouts/CustomBarChart";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import MaskingAudioController from "../therapy/MaskingAudioController";
import { getPatientByIdApi } from "../../apis/patient.api";
import group from "../../assets/images/Group.svg";
import INFO_ICON from "../../assets/images/info-circle.svg";
import {
  fetchDailyUsageTimeApi,
  fetchUsageTimeApi,
  fetchUsageTimeByTherapyApi,
} from "../../apis/usageAppTime";
import moment from "moment";

import CustomDialog from "../../components/layouts/common/CustomDialog";
import { closeModal, openModal } from "../../store/actions/modalAction";
import PatientInformationView from "../patient details dashboard/PatientInformationView";
import { GENDER } from "../../utils/constants";
import { QrCodeModal } from "../patient details dashboard/PatientQrCode";
import { calculateAge, findObjectKeyByValue } from "../../utils/helper";
import { FlexedColumnBox } from "../../components/layouts/common/boxes";
import { FilterTitleBox } from "../../components/layouts/OneViewBox";
import PopUpModal from "../../components/Modal";

const PatientHearingDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();

  // const maskingButton = () =>{
  //   dispatch(openModal(<MaskingAudioController />, "lg", false, "masking"))
  //   navigate("/therapy");
  // }
  const [loading, setLoading] = useState([]);
  const [patient, setPatient] = useState({});
  const [therapyUsage, setTherapyUsage] = useState([]);

  const [todayUsage, setTodayUsage] = useState({
    value: 0,
    gradientColors: ["#F2653A", "#AA218E"],
  });
  const [weeklyUsage, setWeeklyUsage] = useState({
    value: 0,
    gradientColors: ["#28A745", "#2185D0"],
  });
  const fetchApi = getPatientByIdApi;

  const onclickButton = () => {
    navigate(`/therapy/${params.id}`, {
      state: { PatientFrequency: parseInt(patient?.frequency) },
    });
  };

  const fetchPatient = (id) => {
    setLoading(true);
    dispatch(
      callApiAction(
        async () => await fetchApi({ id: params.id }),
        (response) => {
          setPatient(response);
          setLoading(false);
        },
        (err) => {
          setLoading(false);
        }
      )
    );
  };

  const fetchDailyUsageTime = (id) => {
    setLoading(true);

    const startDate = moment().startOf("day").toISOString();
    const endDate = moment().endOf("day").toISOString();

    // console.log("Fetching daily data...");

    // Fetch Daily Usage Data
    dispatch(
      callApiAction(
        async () =>
          await fetchDailyUsageTimeApi({
            patient_id: id,
            startDate,
            endDate,
          }),
        (dailyResponse) => {
          // console.log("Daily Response:", dailyResponse); // Debugging

          if (dailyResponse && dailyResponse.length > 0) {
            const dailyData = dailyResponse[0]; // Ensure correct data structure

            const todayUsageMinutes =
              (dailyData.hours ?? 0) * 60 + (dailyData.minutes ?? 0);
            const selectedSchedule = dailyData.selected_schedule ?? 0;

            // console.log("Today Usage:", todayUsageMinutes);
            // console.log("Selected Schedule:", selectedSchedule);

            setTodayUsage({
              value: todayUsageMinutes,
              maxValue: selectedSchedule,
              displayValue: formatTime(todayUsageMinutes),
              gradientColors: ["#F2653A", "#AA218E"],
            });
          } else {
            console.warn("No daily data received");
            setTodayUsage({
              value: 0,
              maxValue: 0,
              displayValue: "0h 0m",
              gradientColors: ["#F2653A", "#AA218E"],
            });
          }

          console.log("Fetching weekly data...");

          // Fetch Weekly Usage Data After Daily Data is Loaded
          dispatch(
            callApiAction(
              async () =>
                await fetchDailyUsageTimeApi({
                  patient_id: id,
                  startDate: endDate,
                  weekly: true,
                }),
              (weeklyResponse) => {
                // console.log("Weekly Response:", weeklyResponse); // Debugging

                if (weeklyResponse && weeklyResponse.length > 0) {
                  const weeklyData = weeklyResponse[0];

                  const weeklyUsageMinutes =
                    (weeklyData.hours ?? 0) * 60 + (weeklyData.minutes ?? 0);
                  const weekSchedule = weeklyData.week_schedule ?? 0;

                  // console.log("Weekly Usage:", weeklyUsageMinutes);
                  // console.log("Week Schedule:", weekSchedule);

                  setWeeklyUsage({
                    value: weeklyUsageMinutes,
                    maxValue: weekSchedule,
                    displayValue: formatTime(weeklyUsageMinutes),
                    gradientColors: ["#28A745", "#2185D0"],
                  });
                } else {
                  console.warn("No weekly data received");
                  setWeeklyUsage({
                    value: 0,
                    maxValue: 0,
                    displayValue: "0h 0m",
                    gradientColors: ["#28A745", "#2185D0"],
                  });
                }

                setLoading(false);
              },
              (err) => {
                console.error("Weekly API Error:", err);
                setLoading(false);
              }
            )
          );
        },
        (err) => {
          console.error("Daily API Error:", err);
          setLoading(false);
        }
      )
    );
  };

  // Helper function to format time
  const formatTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  useEffect(() => {
    fetchDailyUsageTime(params.id);
  }, []);

  const fetchTherapyUsage = (id) => {
    setLoading(true);

    const startDate = moment().startOf("day").toISOString();
    const endDate = moment().endOf("day").toISOString();
    dispatch(
      callApiAction(
        async () =>
          await fetchUsageTimeByTherapyApi({
            patient_id: id,
            startDate,
            endDate,
          }),
        (response) => {

          if (response && response.length > 0) {
            const formattedData = response.map((item) => ({
              name: item.therapy_id ? item.therapy_id : item.name || "Unknown Therapy",
              usage: item.total_usage_time
                ? Math.floor(item.total_usage_time / 60000)
                : 0, // Convert ms to minutes
              maxUsage: item.selected_schedule ?? 0,
            }));

            setTherapyUsage(formattedData);
          } else {
            setTherapyUsage([]);
          }

          setLoading(false);
        },
        (err) => {
          console.error("Therapy API Error:", err);
          setLoading(false);
        }
      )
    );
  };

  const [selectedDate, setSelectedDate] = useState(moment());
  const [isMonthly, setIsMonthly] = useState(true);
  const [usageData, setUsageData] = useState({});

  const fetchUsageChartData = (selectedDate, isMonthlyView) => {
    if (!selectedDate || !moment.isMoment(selectedDate)) return;

    const startDate = isMonthlyView
      ? selectedDate.clone().startOf("month").toISOString()
      : selectedDate.clone().startOf("week").toISOString();

    const endDate = isMonthlyView
      ? selectedDate.clone().endOf("month").toISOString()
      : selectedDate.clone().endOf("week").toISOString();

    dispatch(
      callApiAction(
        async () =>
          await fetchUsageTimeApi({
            patient_id: params.id,
            startDate,
            endDate,
          }),
        (response) => {
          const usageMap = {};
          response?.forEach((item) => {
            const dateKey = moment(item.date).format("YYYY-MM-DD");
            usageMap[dateKey] = item.minutes || 0; // Directly store minutes value
          });

          setUsageData(usageMap);
        }
      )
    );
  };

  useEffect(() => {
    fetchUsageChartData(selectedDate, isMonthly);
  }, [selectedDate, isMonthly]);

  useEffect(() => {
    fetchPatient();
    fetchUsageChartData();
  }, []);
  const therapies = [
    { name: "Relaxed Home", usage: 62 },
    { name: "Birds Mixer", usage: 57 },
    { name: "Nature Jungle", usage: 59 },
    { name: "Rainy with birds", usage: 55 },
    { name: "Garden", usage: 53 },
  ];

  useEffect(() => {
    fetchTherapyUsage(params.id);
  }, []);

  const handleDateNavigation = (direction) => {
    setSelectedDate((prev) => {
      if (!prev || !moment.isMoment(prev)) return moment(); // Ensure prev is always a moment object

      const newDate = isMonthly
        ? prev.clone().add(direction, "month")
        : prev.clone().add(direction, "week");

      fetchUsageChartData(newDate, isMonthly); // Fetch new data
      return newDate;
    });
  };

  const generateMonthlyData = () => {
    const startOfMonth = selectedDate.clone().startOf("month");
    const endOfMonth = selectedDate.clone().endOf("month");

    const days = [];
    let currentDay = startOfMonth.clone();

    while (currentDay.isSameOrBefore(endOfMonth, "day")) {
      const dateKey = currentDay.format("YYYY-MM-DD");
      days.push({
        label: {
          day: currentDay.format("dd"),
          date: currentDay.format("D"),
        },
        value: usageData[dateKey] || 0,
        isCurrentMonth: true,
      });

      currentDay.add(1, "day");
    }
    return days;
  };

  const generateWeeklyData = () => {
    const startOfWeek = selectedDate.clone().startOf("week");

    return Array.from({ length: 7 }, (_, i) => {
      const date = startOfWeek.clone().add(i, "day");
      return {
        label: {
          day: date.format("dd"), // Mo, Tu, We...
          date: date.format("D"),
        },
        value: usageData[date.format("YYYY-MM-DD")] || 0, // Fill missing dates with 0
      };
    });
  };

  /** Correctly formats navigation header */
  const getDateNavigatorLabel = () => {
    if (isMonthly) {
      const previousMonth = selectedDate.clone().subtract(1, "month");
      return `${selectedDate.format("MMMM YYYY")}`;
    } else {
      const startOfWeek = selectedDate.clone().startOf("week");
      const endOfWeek = selectedDate.clone().endOf("week");
      return `${startOfWeek.format("D MMM")} - ${endOfWeek.format(
        "D MMM YYYY"
      )}`;
    }
  };

  useEffect(() => {
    const startDate = isMonthly
      ? selectedDate.clone().startOf("month").toISOString()
      : selectedDate.clone().startOf("week").toISOString();
    const endDate = isMonthly
      ? selectedDate.clone().endOf("month").toISOString()
      : selectedDate.clone().endOf("week").toISOString();

    fetchUsageChartData(startDate, endDate);
  }, [selectedDate, isMonthly]);
  const title = "Patient details";

  const infoIconView = (data) => {
    console.log("Opening Modal for Patient:", data); 
    dispatch(
      openModal(<PatientInformationView data={data} />, "md", false, "information")
    );
  };
const todayDate = moment().format("DD MMM YYYY");
  return (
    <>
      <PopUpModal />
      <Box sx={{ backgroundColor: "white",  padding: "0px 12px", }}>
        {/* Header Section */}
        <Box
          mb={1}
          sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}
        >
          <Box
            sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}
          >
            <IconButton
              sx={{ marginRight: "10px" }}
              onClick={() => {
                navigate("/patient");
              }}
            >
              <ArrowBackIosNew sx={{ color: "#424242" }} />
              <Typography
                ml={2}
                className="AxiformaRegular"
                sx={{
                  fontSize: "24px",
                  alignItems: "center",
                  color: "#424242",
                }}
              >
                Back
              </Typography>
            </IconButton>{" "}
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
            }}
          >
            <Typography
              className="AxiformaMedium"
              variant="h6"
              ml={2}
              sx={{
                fontSize: "24px",
                alignItems: "center",
                letterSpacing: "0.8em",
                color: "#424242",
              }}
            >
              PATIENT PROFILE
            </Typography>
          </Box>
        </Box>
        {/* Profile and Actions Section */}
        <Grid2
          sx={{
            width: "100%",
            backgroundColor: "white",
            border: "1px solid #DEDEDE",
            borderRadius: "10px",
            maxHeight: "630px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "3px",
              maxHeight: "58px",
            }}
          >
            {/* User Information */}
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <img src={group}></img>

              <Typography
                sx={{
                  fontSize: "20px",
                  color: "#424242",
                  fontWeight: "500",
                  ml: 4,
                }}
              >
                {patient.name}
              </Typography>

              <IconButton
                onClick={(e) => {
                  infoIconView(patient);
                }}
              >
                <img src={INFO_ICON} />
              </IconButton>
            </Box>

            {/* Actions */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                marginRight: "20px",
              }}
            >
          <Box sx={{ marginRight: "40px" }}>
  {loading ? (
    <CircularProgress size={24} />
  ) : patient.frequency ? (
    <>
      <Typography
        sx={{
          color: "#424242",
          fontSize: "16px",
          fontWeight: "500",
          mr: 3,
        }}
      >
        Tinnitus Frequency
      </Typography>
      <Typography
        sx={{
          color: "#E53935",
          fontSize: "22px",
          fontWeight: "700",
          mr: 3,
        }}
      >
        {patient.frequency} Hz
        <IconButton
          onClick={() => {
            navigate(`/tinnitusTest/${params.id}`);
          }}
        >
          <Refresh sx={{ color: "#E53935" }} />
        </IconButton>
      </Typography>
    </>
  ) : (
    <Button
      variant="outlined"
      sx={{
        border: "1px solid #424242",
        color: "#424242",
        textTransform: "none",
        padding: "7px 19px",
      }}
      onClick={() => {
        navigate(`/tinnitusTest/${params.id}`);
      }}
      disabled={loading}
    >
      {loading ? <CircularProgress size={20} /> : <Typography sx={{ fontSize: "16px" }}>Take a Hearing Test</Typography>}
    </Button>
  )}
</Box>


<Button
  onClick={onclickButton}
  variant="contained"
  sx={{
    backgroundColor: loading ? "#BDBDBD" : patient.frequency ? "#424242" : "#BDBDBD",
    color: "white",
    fontWeight: "500",
    textTransform: "none",
    padding: "8px 44px",
  }}
  disabled={loading || !patient.frequency}
>
  {loading ? <CircularProgress size={20} color="inherit" /> : <Typography sx={{ fontSize: "16px" }}>Create Therapy</Typography>}
</Button>
            </Box>
          </Box>

          {/* Divider */}
          <Divider />

          {/* Main Content */}
          <Grid container alignItems="stretch">
            {/* Left Section */}
            <Grid
              item
              xs={12}
              md={6}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 3,
                height: "80%",
                borderRight: "1px solid #DEDEDE",
              }}
            >
              {/* Circular Charts Area */}
              <Box
                sx={{
                  width: "100%",
                  height: "240px",
                  borderBottom: "1px solid #DEDEDE",
                  display: "flex",
                  justifyContent: "space-between",

                }}
              >
                {/* Today's Usage Chart */}
                <Box
                  sx={{
                    flex: 1,
                    borderRight: "1px solid #DEDEDE",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    position: "relative",
                  }}
                >
                  <Box
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            backgroundColor: "#F5F5F5",
            border: "1px solid #DBDBDB",
            borderRadius: "3px",
            px: 1,
            py: 0.5,
          }}
        >
        <Typography sx={{ fontSize: "12px", fontWeight: "500", color: "#424242" }}>
          {todayDate}
        </Typography>
      </Box>
                  <UsageChart
                    value={todayUsage.value}
                    maxValue={todayUsage.maxValue}
                    displayValue={todayUsage.displayValue}
                    title="Today's Usage"
                  />{" "}
                </Box>

                {/* Weekly Usage Chart */}
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <UsageChart
                    value={weeklyUsage.value}
                    maxValue={weeklyUsage.maxValue}
                    displayValue={weeklyUsage.displayValue}
                    title="Weekly Usage"
                  />
                </Box>
              </Box>
              <Grid2
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  borderBottom: "1px solid #DEDEDE",
                  padding: "10px",
                }}
              >
                <Typography
                  sx={{
                    justifyContent: "center",
                    fontSize: "20px",
                    alignItems: "center",
                    mb: "5px",
                  }}
                >
                  Therapy
                </Typography>
              </Grid2>
              {/* Therapy List */}
              <Grid2
                sx={{
                  paddingTop: "8px",
                  display: "flex",
                  flexDirection: "column",
                  paddingRight: "20px",
                  paddingLeft: "20px",
                  maxHeight: "240px",
                  overflowY: "auto",
                  overflowX: "hidden",
                  minHeight: "250px",

                  "&::-webkit-scrollbar": {
                    width: "10px",
                  },

                  "&::-webkit-scrollbar-thumb": {
                    background: "#DCDCDC",
                    borderRadius: "10px",
                    minHeight: "20px",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    background: "#B33D3D",
                  },
                }}
              >
                {therapyUsage.length === 0 ? (
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#757575",
                      fontWeight: 500,
                      textAlign: "center",
                      justifyContent: "center",

                      fontSize: "22px",
                      flexGrow: 1, // Ensures it fills the parent
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    No therapy available
                  </Typography>
                ) : (
                  therapyUsage.map((therapy, index) => (
                    <Grid2
                      key={index}
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      {/* Therapy Name */}
                      <Typography
                        variant="body2"
                        sx={{ color: "#424242", fontWeight: 500 }}
                      >
                        {therapy.name}
                      </Typography>

                      {/* Progress Bar */}
                      <Grid2
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <Tooltip title={`${therapy.usage} minutes used`} arrow>
                          <LinearProgress
                            variant="determinate"
                            value={(therapy.usage / therapy.maxUsage) * 100}
                            sx={{
                              flex: 1,
                              height: "8px",
                              borderRadius: "4px",
                              backgroundColor: "#F0F0F0",
                              "& .MuiLinearProgress-bar": {
                                backgroundColor: "#D34646",
                              },
                            }}
                          />
                        </Tooltip>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#757575",
                            fontWeight: 400,
                            paddingLeft: "24px",
                          }}
                        >
                          {therapy.maxUsage}m
                        </Typography>
                      </Grid2>
                    </Grid2>
                  ))
                )}
              </Grid2>
            </Grid>

            {/* Right Section */}
            <Grid
              item
              xs={12}
              md={6}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 3,
              }}
            >
              {/* Date Picker with Tabs */}
              <Grid2
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  borderBottom: "1px solid #DEDEDE",
                  padding: "10px",
                }}
              >
                {/* Date Picker Section */}
                <Grid2
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexGrow: 1,
                    justifyContent: "center",
                    pl: "120px",
                  }}
                >
                  <IconButton onClick={() => handleDateNavigation(-1)}>
                    <ArrowBackIos />
                  </IconButton>
                  <Typography variant="h6">
                    {getDateNavigatorLabel()}
                  </Typography>
                  <IconButton onClick={() => handleDateNavigation(1)}>
                    <ArrowForwardIos />
                  </IconButton>
                </Grid2>

                {/* Tabs Section */}
                <Grid2
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                  }}
                >
                  <Tabs
                    value={isMonthly ? 1 : 0}
                    onChange={(event, newValue) => setIsMonthly(newValue === 1)}
                    textColor="primary"
                    sx={{
                      "& .MuiTabs-indicator": {
                        display: "none",
                      },
                    }}
                  >
                    <Tab
                      label="Weekly"
                      sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}
                    />
                    <Tab
                      label="Monthly"
                      sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}
                    />
                  </Tabs>
                </Grid2>
              </Grid2>

              {/* Bar Chart */}
              <Box sx={{ flex: 1, padding: "10px" }}>
                <CustomBarChart
                  data={
                    isMonthly ? generateMonthlyData() : generateWeeklyData()
                  }
                  highlightedIndex={
                    isMonthly ? selectedDate.date() - 1 : selectedDate.day()
                  }
                  barSize={isMonthly ? 10 : 60}
                />
              </Box>
            </Grid>
          </Grid>
        </Grid2>
      </Box>
    </>
  );
};

export default PatientHearingDetails;
